export type WebVitalName = "LCP" | "CLS" | "INP" | "TTFB";
export type WebVitalRating = "good" | "needs-improvement" | "poor";

export interface WebVitalMetric {
  name: WebVitalName;
  value: number;
  rating: WebVitalRating;
  navigation_type: string;
  measurement_version: number;
}

export const WEB_VITAL_MEASUREMENT_VERSION = 2;

interface LayoutShiftEntry extends PerformanceEntry {
  hadRecentInput: boolean;
  value: number;
}

interface LayoutShiftSample {
  startTime: number;
  value: number;
}

interface EventTimingEntry extends PerformanceEntry {
  interactionId: number;
}

const thresholds: Record<WebVitalName, readonly [number, number]> = {
  LCP: [2_500, 4_000],
  CLS: [0.1, 0.25],
  INP: [200, 500],
  TTFB: [800, 1_800],
};

export function rateWebVital(name: WebVitalName, value: number): WebVitalRating {
  const [good, poor] = thresholds[name];
  if (value <= good) return "good";
  if (value <= poor) return "needs-improvement";
  return "poor";
}

export function p98InteractionLatency(values: readonly number[]) {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.max(0, Math.ceil(sorted.length * 0.98) - 1)];
}

/**
 * Track CLS using the current Core Web Vitals session-window definition:
 * consecutive shifts belong to one window only while adjacent entries are
 * less than one second apart and the entire window remains under five seconds.
 * The reported score is the largest window, not the lifetime sum.
 */
export function createLayoutShiftSessionTracker() {
  let windowStart = -1;
  let previousShift = -1;
  let windowScore = 0;
  let maximumScore = 0;

  return {
    add(entry: LayoutShiftSample) {
      if (!Number.isFinite(entry.startTime) || !Number.isFinite(entry.value) || entry.value < 0) {
        return maximumScore;
      }

      const startsNewWindow = windowStart < 0
        || entry.startTime - previousShift >= 1_000
        || entry.startTime - windowStart >= 5_000;

      if (startsNewWindow) {
        windowStart = entry.startTime;
        windowScore = entry.value;
      } else {
        windowScore += entry.value;
      }

      previousShift = entry.startTime;
      maximumScore = Math.max(maximumScore, windowScore);
      return maximumScore;
    },
    value() {
      return maximumScore;
    },
  };
}

function roundedValue(name: WebVitalName, value: number) {
  return name === "CLS" ? Math.round(value * 1_000) / 1_000 : Math.round(value);
}

/**
 * Capture one privacy-safe field-performance sample for the initial page load.
 * No element selectors, URLs, user input, or device identifiers are collected.
 */
export function startWebVitals(report: (metrics: WebVitalMetric[]) => void) {
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return () => undefined;

  const supported = new Set(PerformanceObserver.supportedEntryTypes ?? []);
  const observers: PerformanceObserver[] = [];
  const interactions = new Map<number, number>();
  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  const navigationType = navigation?.type ?? "navigate";
  let lcp: number | undefined;
  const cls = createLayoutShiftSessionTracker();
  let clsSupported = false;
  let reported = false;

  const observe = (type: string, callback: PerformanceObserverCallback, options?: PerformanceObserverInit) => {
    if (!supported.has(type)) return;
    try {
      const observer = new PerformanceObserver(callback);
      observer.observe(options ?? { type, buffered: true });
      observers.push(observer);
    } catch {
      // Browser support differs; omit unavailable metrics instead of affecting the app.
    }
  };

  observe("largest-contentful-paint", (list) => {
    const last = list.getEntries().at(-1);
    if (last) lcp = last.startTime;
  });

  if (supported.has("layout-shift")) clsSupported = true;
  observe("layout-shift", (list) => {
    for (const entry of list.getEntries() as LayoutShiftEntry[]) {
      if (!entry.hadRecentInput) cls.add(entry);
    }
  });

  observe("event", (list) => {
    for (const entry of list.getEntries() as EventTimingEntry[]) {
      if (!entry.interactionId) continue;
      interactions.set(entry.interactionId, Math.max(interactions.get(entry.interactionId) ?? 0, entry.duration));
    }
  }, { type: "event", buffered: true, durationThreshold: 40 } as PerformanceObserverInit);

  const finish = () => {
    if (reported) return;
    reported = true;
    const metrics: WebVitalMetric[] = [];
    const add = (name: WebVitalName, rawValue: number | undefined) => {
      if (rawValue === undefined || !Number.isFinite(rawValue) || rawValue < 0) return;
      const value = roundedValue(name, rawValue);
      metrics.push({
        name,
        value,
        rating: rateWebVital(name, value),
        navigation_type: navigationType,
        measurement_version: WEB_VITAL_MEASUREMENT_VERSION,
      });
    };

    add("TTFB", navigation?.responseStart);
    add("LCP", lcp);
    if (clsSupported) add("CLS", cls.value());
    if (interactions.size > 0) add("INP", p98InteractionLatency([...interactions.values()]));
    if (metrics.length > 0) report(metrics);

    observers.forEach((observer) => observer.disconnect());
    window.clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange, true);
    window.removeEventListener("pagehide", finish);
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") finish();
  };

  const timer = window.setTimeout(finish, 30_000);
  document.addEventListener("visibilitychange", onVisibilityChange, true);
  window.addEventListener("pagehide", finish);

  return finish;
}
